import {
  Button,
  Stack,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import Radio from 'common/Radio';
import Calendar from 'common/DatePicker';
import Divider from '@mui/material/Divider';
import SortableList, { SortableItem } from 'react-easy-sort';
import { arrayMoveImmutable } from 'array-move';
import TextareaEditor from 'common/Editor';

import { TitlesWrapper, VisibleWrapper, StyledFormHelperText } from './styled';
import { useCreateRecipeLogic } from './useCreateRecipeLogic';
import MultipleInstructions from './components/MultipleInstructions';
import { recipeFirstKey } from './helper/constant';
import { makeInstructionsInitData } from './helper/data';
import ProductImage from './components/ProductImage';
import ProductsList from './components/ProductsList';
import { IRecipeInstructions } from './helper/types';
import IngredientsList from './components/IngredientsList';

const RecipeMutation = () => {
  const {
    paramId,
    isLoading,
    methods,
    errors,
    isSuccess,
    isImageViewOpen,
    isButtonLoading,
    imageSrcForAction,
    isDeleteModalOpen,
    largeImageMessage,
    instructions,
    watch,
    handleDeleteImage,
    setValue,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    onChangeMultipleUpload,
    onChangeSingleUpload,
  } = useCreateRecipeLogic();

  if (isLoading) {
    return <div>Loading</div>
  }

  return (
    <FormProvider {...methods}>
      <Stack
        onSubmit={handleSubmit(onSubmit)}
        component="form"
      >
        <Typography
          variant="h5"
          sx={{ mb: 4 }}
        >
          { paramId ? 'Edit Recipe' : 'Create New Recipe'}
        </Typography>

        <TitlesWrapper>
          <TextField
            {...register('titleAm')}
            label="Title Am"
            error={!!errors.titleAm}
            helperText={errors.titleAm ? 'Title is required' : ''}
          />
          <TextField
            {...register('titleEn')}
            label="Title En"
            error={!!errors.titleEn}
            helperText={errors.titleEn ? 'Title is required' : ''}
          />

          <TextField
            {...register('titleRu')}
            label="Title Ru"
            error={!!errors.titleRu}
            helperText={errors.titleRu ? 'Title is required' : ''}
          />

          <TextareaEditor
            value={watch(`descriptionAm`)}
            label="Description Am"
            name="descriptionAm"
            setValue={setValue}
            error={errors?.descriptionAm?.message}
          />
          <TextareaEditor
            value={watch(`descriptionEn`)}
            label="Description En"
            name="descriptionEn"
            setValue={setValue}
            error={errors?.descriptionEn?.message}
          />
          <TextareaEditor
            value={watch(`descriptionRu`)}
            label="Description Ru"
            name="descriptionRu"
            setValue={setValue}
            error={errors?.descriptionRu?.message}
          />
          <TextareaEditor
            value={watch(`ingredientsAm`)}
            label="Ingredients Am"
            name="ingredientsAm"
            setValue={setValue}
            error={errors?.ingredientsAm?.message}
          />
          <TextareaEditor
            value={watch(`ingredientsEn`)}
            label="Ingredients En"
            name="ingredientsEn"
            setValue={setValue}
            error={errors?.ingredientsEn?.message}
          />
          <TextareaEditor
            value={watch(`ingredientsRu`)}
            label="Ingredients Ru"
            name="ingredientsRu"
            setValue={setValue}
            error={errors?.ingredientsRu?.message}
          />

        </TitlesWrapper>
        <IngredientsList recipeId={paramId} />
        <Divider />
        <ProductsList />
        <Divider />
        <ProductImage
          setFlowProcess={setFlowProcess}
          onChangeSingleUpload={onChangeSingleUpload}
        />
        {
            instructions?.map((field, instIndex) => {
              const imageDeleting = (base64: string) => {
                handleDeleteImage(base64, field.id)
              }

              return (
                <MultipleInstructions
                  key={field?.id || JSON.stringify(field) + instIndex}
                  id={field.id}
                  index={instIndex}
                  handleDeleteImage={imageDeleting}
                  isImageViewOpen={isImageViewOpen}
                  imageSrcForAction={imageSrcForAction}
                  isDeleteModalOpen={isDeleteModalOpen}
                  setFlowProcess={setFlowProcess}
                  onChangeMultipleUpload={onChangeMultipleUpload}
                />
              )
            })
          }
        <Divider sx={{ mt: 1 }} />
        <Button
          sx={{ mt: 1, maxWidth: "300px" }}
          type="button"
          color="primary"
          variant="contained"
          disabled={isButtonLoading}
          onClick={() => {
            setValue(recipeFirstKey, [...instructions, makeInstructionsInitData()])
          }}
        >
          { instructions.length === 0 ? 'Add instruction for recipe' : 'Add one more instruction' }
        </Button>
        <VisibleWrapper>
          <Box>
            <Typography>
              New
            </Typography>
            <Box>
              <Radio label="Yes" name="new" canBe />
              <Radio label="No" name="new" canBe={false} />
            </Box>
          </Box>
          <Box>
            <Typography>
              Popular
            </Typography>
            <Box>
              <Radio label="Yes" name="popular" canBe />
              <Radio label="No" name="popular" canBe={false} />
            </Box>
          </Box>
        </VisibleWrapper>
        <VisibleWrapper>
          <Box>
            <Typography>
              Visibility in website
            </Typography>
            <Box>
              <Radio label="Yes" name="visible" canBe />
              <Radio label="No" name="visible" canBe={false} />
            </Box>
          </Box>
          <Box>
            <Typography>
              In Home
            </Typography>
            <Box>
              <Radio label="Yes" name="activeInHome" canBe />
              <Radio label="No" name="activeInHome" canBe={false} />
            </Box>
          </Box>
          <Box>
            <Calendar name="postedDate" label="Date" />
          </Box>
          <Box>
            <TextField
              type="number"
              {...register('minRead')}
              label="Read Minutes"
              error={!!errors.minRead}
              helperText={errors.minRead ? 'Minute is required' : ''}
            />
          </Box>
          <Box>
            <TextField
              type="number"
              {...register('minMake')}
              label="Make Minutes"
              error={!!errors.minMake}
              helperText={errors.minMake ? 'Minute is required' : ''}
            />
          </Box>
        </VisibleWrapper>
        {largeImageMessage && <StyledFormHelperText id="messages">{largeImageMessage}</StyledFormHelperText>}
        <p>{isSuccess}</p>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{ mt: 1 }}
          disabled={isButtonLoading}
        >
          Submit
        </Button>
      </Stack>
    </FormProvider>
  );
};

export default RecipeMutation;
